import React, { useCallback, useEffect, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { View, StyleSheet, TouchableOpacity, ActivityIndicator, ScrollView } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import * as Clipboard from 'expo-clipboard';
import { Typography } from '../ui';
import { useTheme } from '../../context/ThemeContext';
import { useI18n } from '../../i18n';
import { useReferralPayoutEnabled } from '../../hooks/useFeatureFlags';
import { refreshPayoutDetailsReminder } from '../../hooks/usePayoutDetailsReminder';
import { api } from '../../services/api';
import { formatReferralMoney } from '../../utils/referralMoney';
import { canEnroll, enrollDocument, needsReaccept, referralTermsGate } from './referralTermsDocument';
import { trackShareClicked, trackShareCompleted } from '../../services/shareAnalytics';
import type { RootStackParamList } from '../../navigation/types';
import {
    ReferralAccrualState,
    type ReferralAccrual,
    type ReferralFunnel,
    type ReferralIdentity,
    type ReferralSummary,
} from '../../types/referral';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../../styles/accentText';
/**
 * CORE-964 — блок «Реферальная программа» на экране Фонда, денежная модель v3.
 *
 * Заменяет прежнюю секцию про «hearts / agapes»: баллов в модели больше нет,
 * вознаграждение — реальные деньги, и показывать вместо них баллы значит
 * обещать человеку то, чего не существует.
 *
 * <p>Что здесь намеренно НЕ считается: ничего. Все суммы приходят с сервера в
 * целых минорных единицах и только форматируются — фронт не складывает, не
 * вычитает и не берёт проценты. Это правило из архитектуры (CORE-955): деньги
 * считаются в одном месте, иначе две реализации формулы разойдутся.</p>
 *
 * <p>Персональных данных приглашённых здесь нет, потому что их нет в ответах
 * API — не потому что мы их прячем.</p>
 */

const TERMS_VERSION = 'v3-2026-08-17';
/**
 * CORE-1662 — при модели баллов человек подключается не к денежным правилам v3,
 * а на условиях Пользовательского соглашения (раздел о баллах, редакция
 * 01.10.2026): запись о подключении не должна говорить, что он принял правила
 * про выплаты.
 */
const TERMS_VERSION_POINTS = 'points-terms-2026-10-01';

/**
 * Сама ссылка в аналитику не уходит: `scrubShareProps` вырезает `url`, `link` и
 * прочие поля со свободным текстом. Отчёту нужно знать, что поделились и
 * откуда, а не чем именно.
 */
const SHARE_TARGET = {
    entityType: 'fund' as const,
    entityKey: 'referral_link',
    surface: 'fund' as const,
};

/**
 * CORE-1521 — до подключения блок показывает только описание и кнопку: ссылка,
 * воронка и начисления — персональные данные программы, и открываются они
 * подключением, а не раньше. Иначе непонятно, что кнопка вообще меняет.
 * Состояние берём с сервера (`identity.enrolled`), поэтому оно переживает
 * перезагрузку и повторный вход без локального хранения.
 */
interface ReferralProgramSectionProps {
    /** Экран с собственной сводкой (Финансы) перечитывает её после подключения. */
    onEnrolled?: () => void;
}

export const ReferralProgramSection: React.FC<ReferralProgramSectionProps> = ({ onEnrolled }) => {
    const { isDark, colors: C } = useTheme();
    const { t } = useI18n();
    const payoutsEnabled = useReferralPayoutEnabled();
    const nav = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const [identity, setIdentity] = useState<ReferralIdentity | null>(null);
    const [summary, setSummary] = useState<ReferralSummary | null>(null);
    const [funnel, setFunnel] = useState<ReferralFunnel | null>(null);
    const [accruals, setAccruals] = useState<ReferralAccrual[]>([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [copied, setCopied] = useState(false);
    const [enrolling, setEnrolling] = useState(false);
    const [enrollFailed, setEnrollFailed] = useState(false);
    // CORE-1071 — галочка «Условий реферальной программы» и их текст по запросу.
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [termsOpen, setTermsOpen] = useState(false);
    const [termsText, setTermsText] = useState<string | null>(null);
    const [termsLoadFailed, setTermsLoadFailed] = useState(false);

    const card = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
    const subtle = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)';

    const loadDetails = useCallback(async (points: boolean) => {
        // CORE-1662 — при модели баллов сумм нет: запрашиваем только воронку.
        if (points) {
            setFunnel(await api.getReferralFunnel());
            return;
        }
        const [s, f, a] = await Promise.all([
            api.getReferralSummary(),
            api.getReferralFunnel(),
            api.getReferralAccruals(5),
        ]);
        setSummary(s);
        setFunnel(f);
        setAccruals(a);
    }, []);

    const load = useCallback(async () => {
        setFailed(false);
        try {
            // Личность нужна всегда, остальное — только для подключённого:
            // до принятия правил сумм ещё нет, и запрашивать их незачем.
            const me = await api.getReferralIdentity();
            setIdentity(me);
            if (me.enrolled) await loadDetails(me.rewardModel === 'points');
        } catch {
            // Сюда попадаем и когда этап выключен (404), и при сетевой ошибке.
            // Различать их пользователю незачем: в обоих случаях показывать
            // нечего, а обещать деньги «попробуйте позже» — нельзя.
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [loadDetails]);

    useEffect(() => { load(); }, [load]);

    const copyLink = useCallback(async () => {
        if (!identity) return;

        // Копирование ссылки — это канал шеринга, и он обязан считаться:
        // в репозитории есть сторож (shareAnalytics-CORE788), который валит
        // сборку, если поверхность дотянулась до буфера обмена молча. Тихая
        // воронка — ровно тот дефект, ради которого сторож и написан.
        trackShareClicked(SHARE_TARGET);
        await Clipboard.setStringAsync(identity.url);
        trackShareCompleted({ ...SHARE_TARGET, channel: 'clipboard', outcome: 'copied' });

        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [identity]);

    const enroll = useCallback(async () => {
        const gate = referralTermsGate(identity ?? {});
        // CORE-1071 — без галочки участие не включается; сервер проверит то же.
        if (!canEnroll(gate, termsAccepted)) return;
        setEnrolling(true);
        setEnrollFailed(false);
        let me: ReferralIdentity;
        try {
            me = await api.enrollInReferralProgram(
                identity?.rewardModel === 'points' ? TERMS_VERSION_POINTS : TERMS_VERSION,
                enrollDocument(gate),
            );
        } catch {
            // Прятать весь блок здесь нельзя: человек нажал кнопку и должен
            // увидеть, что не вышло, и иметь возможность нажать ещё раз.
            setEnrollFailed(true);
            setEnrolling(false);
            return;
        }
        setIdentity(me);
        setEnrolling(false);
        onEnrolled?.();
        // CORE-1524 — только что подключился: если выплаты уже включены, а
        // реквизитов нет, напоминание у «Финансов» должно появиться сразу.
        refreshPayoutDetailsReminder();
        // Подключение уже состоялось: если цифры не дочитались, ссылка всё
        // равно нужна, а суммы останутся прочерками до следующего захода.
        if (me.enrolled) loadDetails(me.rewardModel === 'points').catch(() => undefined);
    }, [identity, termsAccepted, loadDetails, onEnrolled]);

    /** CORE-1071 — текст той самой редакции из реестра, под которой стоит галочка. */
    const toggleTerms = useCallback(async () => {
        const gate = referralTermsGate(identity ?? {});
        if (gate.kind !== 'checkbox') return;
        const next = !termsOpen;
        setTermsOpen(next);
        if (!next || termsText !== null) return;
        setTermsLoadFailed(false);
        try {
            const { documentId, version, variant } = gate.document;
            const edition = await api.getLegalEdition(documentId, version, variant);
            setTermsText(edition.text);
        } catch {
            setTermsLoadFailed(true);
        }
    }, [identity, termsOpen, termsText]);

    if (loading) {
        return (
            <View style={[styles.section, styles.centered, { borderTopColor: border }]}>
                <ActivityIndicator color={ACCENT_TEXT} />
            </View>
        );
    }

    if (failed || !identity) return null;

    const totals = summary?.totals ?? [];
    // CORE-1662 — обычному пользователю российского контура положены баллы:
    // ни сумм, ни выплат, ни реквизитов этот блок ему не показывает.
    const points = identity.rewardModel === 'points';
    const termsGate = referralTermsGate(identity);
    const enrollAllowed = canEnroll(termsGate, termsAccepted);

    const reaccept = needsReaccept(identity);

    // CORE-1071 / CORE-2078 — галочка с текстом редакции: и при подключении,
    // и при повторном принятии новой редакции уже подключённым.
    const termsCheckbox = (
        <View style={styles.terms}>
            <TouchableOpacity
                style={styles.termsRow}
                onPress={() => setTermsAccepted(v => !v)}
                activeOpacity={0.8}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: termsAccepted }}
                testID="referral-terms-checkbox"
            >
                <Ionicons
                    name={termsAccepted ? 'checkbox' : 'square-outline'}
                    size={22}
                    color={termsAccepted ? ACCENT_TEXT : C.textSecondary}
                />
                <Typography variant="bodySmall" style={[styles.termsLabel, { color: C.textPrimary }]}>
                    {t('referral.termsAccept')}
                </Typography>
            </TouchableOpacity>
            <TouchableOpacity
                onPress={toggleTerms}
                accessibilityRole="button"
                testID="referral-terms-open"
            >
                <Typography variant="bodySmall" style={[styles.termsLink, { color: ACCENT_TEXT }]}>
                    {t(termsOpen ? 'referral.termsHide' : 'referral.termsRead')}
                </Typography>
            </TouchableOpacity>
            {termsOpen && (
                <ScrollView
                    style={[styles.termsBox, { backgroundColor: subtle, borderColor: border }]}
                    contentContainerStyle={styles.termsBoxContent}
                    nestedScrollEnabled
                >
                    {termsText !== null ? (
                        <Typography variant="bodySmall" style={{ color: C.textSecondary }}>
                            {termsText}
                        </Typography>
                    ) : termsLoadFailed ? (
                        <Typography variant="bodySmall" style={{ color: C.textSecondary }}>
                            {t('referral.termsLoadFailed')}
                        </Typography>
                    ) : (
                        <ActivityIndicator color={ACCENT_TEXT} />
                    )}
                </ScrollView>
            )}
        </View>
    );

    return (
        <View style={[styles.section, { borderTopColor: border }]}>
            <View style={styles.header}>
                <Typography variant="h2" style={[styles.title, { color: C.textPrimary }]}>
                    {t('referral.title')}
                </Typography>
                <Typography variant="body" style={[styles.lead, { color: C.textSecondary }]}>
                    {t(points ? 'referral.leadPoints' : 'referral.lead')}
                </Typography>
            </View>

            {/* ── Подключение к правилам ── */}
            {!identity.enrolled && (
                <>
                    {/* ── CORE-1071: «Условия реферальной программы» ── */}
                    {termsGate.kind === 'checkbox' && termsCheckbox}
                    {termsGate.kind === 'unavailable' && (
                        <Typography variant="bodySmall" style={[styles.termsNote, { color: C.textSecondary }]}>
                            {t('referral.termsUnavailable')}
                        </Typography>
                    )}
                    <TouchableOpacity
                        style={[
                            styles.enrollCta,
                            { backgroundColor: '#E85D04' },
                            !enrollAllowed && styles.enrollCtaDisabled,
                        ]}
                        onPress={enroll}
                        disabled={enrolling || !enrollAllowed}
                        accessibilityState={{ disabled: enrolling || !enrollAllowed }}
                        activeOpacity={0.85}
                        accessibilityRole="button"
                        testID="referral-enroll"
                    >
                        <Typography variant="button" style={{ color: ON_ACCENT_TEXT }}>
                            {enrolling ? t('referral.enrolling') : t('referral.join')}
                        </Typography>
                    </TouchableOpacity>
                    {enrollFailed && (
                        <Typography
                            variant="bodySmall"
                            style={[styles.enrollError, { color: C.textSecondary }]}
                        >
                            {t('referral.enrollFailed')}
                        </Typography>
                    )}
                </>
            )}

            {identity.enrolled && <>
                {/* ── CORE-2078: повторное принятие действующей редакции ── */}
                {reaccept && (
                    <View
                        style={[styles.reaccept, { backgroundColor: subtle, borderColor: border }]}
                        testID="referral-terms-reaccept"
                    >
                        <Typography variant="body" style={[styles.reacceptTitle, { color: C.textPrimary }]}>
                            {t('referral.termsReacceptTitle')}
                        </Typography>
                        <Typography variant="bodySmall" style={{ color: C.textSecondary }}>
                            {t('referral.termsReacceptLead')}
                        </Typography>
                        {termsCheckbox}
                        <TouchableOpacity
                            style={[
                                styles.enrollCta,
                                { backgroundColor: '#E85D04' },
                                !enrollAllowed && styles.enrollCtaDisabled,
                            ]}
                            onPress={enroll}
                            disabled={enrolling || !enrollAllowed}
                            accessibilityState={{ disabled: enrolling || !enrollAllowed }}
                            activeOpacity={0.85}
                            accessibilityRole="button"
                            testID="referral-terms-reaccept-submit"
                        >
                            <Typography variant="button" style={{ color: ON_ACCENT_TEXT }}>
                                {enrolling ? t('referral.termsReacceptSaving') : t('referral.termsReacceptCta')}
                            </Typography>
                        </TouchableOpacity>
                        {enrollFailed && (
                            <Typography
                                variant="bodySmall"
                                style={[styles.enrollError, { color: C.textSecondary }]}
                            >
                                {t('referral.enrollFailed')}
                            </Typography>
                        )}
                    </View>
                )}

                {/* ── Персональная ссылка ── */}
                <View style={[styles.linkBox, { backgroundColor: subtle, borderColor: border }]}>
                    <Typography variant="caption" style={styles.linkLabel}>
                        {t('referral.yourLink')}
                    </Typography>

                    <TouchableOpacity
                        style={[styles.copyArea, { backgroundColor: card }]}
                        onPress={copyLink}
                        activeOpacity={0.8}
                        accessibilityRole="button"
                        accessibilityLabel={t('referral.copyLink')}
                    >
                        <Typography
                            variant="body"
                            style={{ flex: 1, color: C.textPrimary }}
                            numberOfLines={1}
                        >
                            {identity.url}
                        </Typography>
                        <View style={[styles.copyIcon, { backgroundColor: copied ? '#25D366' : '#E98245' }]}>
                            {/* Белый глиф и на оранжевом, и на зелёном #25D366 ниже 3:1 — тёмный на обоих. */}
                            <Ionicons name={copied ? 'checkmark' : 'copy'} size={18} color={ON_ACCENT_TEXT} />
                        </View>
                    </TouchableOpacity>
                </View>

                {/* ── Воронка ── */}
                {funnel && (
                    <View style={styles.statsRow}>
                        {[
                            { label: t('referral.clicks'), value: funnel.clicks, icon: 'link-outline' as const },
                            { label: t('referral.registrations'), value: funnel.registrations, icon: 'person-add-outline' as const },
                            { label: t('referral.buyers'), value: funnel.buyers, icon: 'bag-check-outline' as const },
                        ].map(stat => (
                            <View key={stat.label} style={[styles.statCard, { backgroundColor: subtle }]}>
                                <Ionicons name={stat.icon} size={18} color={ACCENT_TEXT} />
                                <Typography variant="h3" style={{ color: C.textPrimary }}>{stat.value}</Typography>
                                <Typography variant="caption" style={{ color: C.textSecondary }}>{stat.label}</Typography>
                            </View>
                        ))}
                    </View>
                )}

                {/* ── Деньги: по валютам отдельно, они не смешиваются ── */}
                {/*
                    Пока начислений нет, сервер не присылает ни одной валюты — и
                    показать «0 ₽» было бы догадкой: контур получателя определяется
                    только к моменту выплаты (CORE-961), а до тех пор рубль он или
                    доллар, никто не знает. Поэтому строки на месте, как требует ТЗ,
                    но вместо суммы прочерк: структура честная, число не выдумано.
                */}
                {!points && totals.length === 0 && (
                    <View style={[styles.moneyBox, { backgroundColor: subtle, borderColor: border }]}>
                        {[t('referral.earned'), t('referral.available'), t('referral.paid')].map(label => (
                            <View key={label} style={styles.moneyRow}>
                                <Typography variant="body" style={{ color: C.textSecondary }}>{label}</Typography>
                                <Typography variant="body" style={{ color: C.textSecondary }}>—</Typography>
                            </View>
                        ))}
                    </View>
                )}

                {!points && totals.map(total => (
                    <View key={total.currency} style={[styles.moneyBox, { backgroundColor: subtle, borderColor: border }]}>
                        {[
                            { label: t('referral.earned'), value: total.earnedMinor },
                            { label: t('referral.available'), value: total.availableMinor },
                            { label: t('referral.paid'), value: total.paidMinor },
                        ].map(row => (
                            <View key={row.label} style={styles.moneyRow}>
                                <Typography variant="body" style={{ color: C.textSecondary }}>{row.label}</Typography>
                                <Typography variant="body" style={{ color: C.textPrimary, fontWeight: '700' }}>
                                    {formatReferralMoney(row.value, total.currency)}
                                </Typography>
                            </View>
                        ))}
                    </View>
                ))}

                {/* CORE-1156 — этап «начисляем, но не платим». Пока `referral_payout`
                    выключен, деньги реальные и растут, но забрать их нельзя, и сказать
                    это должен интерфейс: иначе «Доступно к выплате» читается как
                    «нажми и получи». */}
                {points && (
                    <View style={[styles.payoutPaused, { backgroundColor: subtle, borderColor: border }]} testID="referral-points-note">
                        <Ionicons name="information-circle-outline" size={18} color={ACCENT_TEXT} />
                        <Typography variant="bodySmall" style={{ color: C.textSecondary, flex: 1 }}>
                            {t('referral.pointsNote')}
                        </Typography>
                    </View>
                )}

                {!points && !payoutsEnabled && (
                    <View style={[styles.payoutPaused, { backgroundColor: subtle, borderColor: border }]} testID="referral-payouts-paused">
                        <Ionicons name="time-outline" size={18} color={ACCENT_TEXT} />
                        <Typography variant="bodySmall" style={{ color: C.textSecondary, flex: 1 }}>
                            {t('referral.payoutsPaused')}
                        </Typography>
                    </View>
                )}

                {/* ── Реквизиты для выплат (CORE-1053) ──
                    Только для подключённых: до принятия правил платить не за что,
                    а форма собирает персональные данные — просить их заранее
                    значит собирать без основания. */}
                {!points && <TouchableOpacity
                    style={[styles.payoutDetailsRow, { borderColor: border }]}
                    onPress={() => nav.navigate('PayoutDetails')}
                    activeOpacity={0.85}
                    accessibilityRole="button"
                >
                    <Ionicons name="card-outline" size={18} color={C.textSecondary} />
                    <Typography variant="body" style={{ color: C.textPrimary, marginLeft: 10, flex: 1 }}>
                        {t('referral.payoutDetails.entry')}
                    </Typography>
                    <Ionicons name="chevron-forward" size={18} color={C.textSecondary} />
                </TouchableOpacity>}

                {/* ── Последние начисления ── */}
                {!points && accruals.length > 0 && (
                    <View style={styles.historyBox}>
                        <Typography variant="h3" style={[styles.historyTitle, { color: C.textPrimary }]}>
                            {t('referral.recentAccruals')}
                        </Typography>

                        {accruals.map((accrual, index) => (
                            <View
                                key={`${accrual.dateUtc}-${index}`}
                                style={[styles.historyRow, { borderBottomColor: border }]}
                            >
                                <View style={styles.historyLeft}>
                                    <Typography variant="body" style={{ color: C.textPrimary }}>
                                        {t(`referral.level${accrual.level}` as any)}
                                    </Typography>
                                    <Typography variant="caption" style={{ color: C.textSecondary }}>
                                        {stateLabel(accrual, t as unknown as (key: string) => string)}
                                    </Typography>
                                </View>
                                <Typography variant="body" style={{ color: C.textPrimary, fontWeight: '700' }}>
                                    {formatReferralMoney(accrual.amountMinor, accrual.currency)}
                                </Typography>
                            </View>
                        ))}
                    </View>
                )}

                {!points && payoutsEnabled && summary?.nextPayoutDateUtc && (
                    <Typography variant="caption" style={[styles.nextPayout, { color: C.textSecondary }]}>
                        {t('referral.nextPayout')} {formatDate(summary.nextPayoutDateUtc)}
                    </Typography>
                )}
            </>}
        </View>
    );
};

/**
 * Подпись состояния. Внутренние состояния бэкенда сюда не просачиваются:
 * человеку нужны «станет доступно», «доступно», «выплачено», «возвращено» и
 * «на проверке», а не корзины ledger'а.
 */
function stateLabel(accrual: ReferralAccrual, t: (key: string) => string): string {
    switch (accrual.state) {
        case ReferralAccrualState.Available:
            return t('referral.stateAvailable');
        case ReferralAccrualState.Paid:
            return t('referral.statePaid');
        case ReferralAccrualState.Returned:
            return t('referral.stateReturned');
        case ReferralAccrualState.OnReview:
            return t('referral.stateOnReview');
        case ReferralAccrualState.Pending:
        default:
            return accrual.availableAtUtc
                ? `${t('referral.statePendingUntil')} ${formatDate(accrual.availableAtUtc)}`
                : t('referral.statePending');
    }
}

function formatDate(iso: string): string {
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
        ? ''
        : date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

const styles = StyleSheet.create({
    section: { borderTopWidth: 1, paddingTop: 50, marginTop: 10, paddingBottom: 40 },
    centered: { alignItems: 'center', justifyContent: 'center', minHeight: 160 },
    header: { alignItems: 'center', marginBottom: 32 },
    title: { textAlign: 'center', marginBottom: 12 },
    lead: { textAlign: 'center', maxWidth: 520 },

    linkBox: { borderRadius: 32, padding: 32, borderWidth: 1 },
    linkLabel: { color: ACCENT_TEXT, marginBottom: 16 },
    copyArea: { flexDirection: 'row', alignItems: 'center', borderRadius: 20, padding: 10, paddingLeft: 20, gap: 12 },
    copyIcon: { width: 44, height: 44, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },

    statsRow: { flexDirection: 'row', gap: 12, marginTop: 20 },
    statCard: { flex: 1, borderRadius: 24, padding: 20, alignItems: 'center', gap: 4 },

    moneyBox: { borderRadius: 24, padding: 24, borderWidth: 1, marginTop: 20, gap: 12 },
    moneyRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },

    enrollCta: { borderRadius: 100, paddingVertical: 16, alignItems: 'center', marginTop: 8 },
    enrollError: { textAlign: 'center', marginTop: 12 },
    enrollCtaDisabled: { opacity: 0.5 },
    reaccept: { borderWidth: 1, borderRadius: 16, padding: 16, marginBottom: 16, gap: 8 },
    reacceptTitle: { fontWeight: '600' },
    terms: { marginTop: 8, gap: 8 },
    termsRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
    termsLabel: { flex: 1 },
    termsLink: { textDecorationLine: 'underline', marginLeft: 32 },
    termsBox: { borderWidth: 1, borderRadius: 12, maxHeight: 280 },
    termsBoxContent: { padding: 12 },
    termsNote: { marginTop: 8 },

    payoutDetailsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderRadius: 14,
        paddingVertical: 14,
        paddingHorizontal: 16,
        marginTop: 16,
    },
    historyBox: { marginTop: 32 },
    historyTitle: { marginBottom: 12 },
    historyRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 16, borderBottomWidth: 1 },
    historyLeft: { gap: 2 },

    nextPayout: { textAlign: 'center', marginTop: 24 },
    payoutPaused: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginTop: 12,
        padding: 12,
        borderRadius: 12,
        borderWidth: 1,
    },
});
